import React, { useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { getLeadScore, TIER_STYLES } from "../../utils/leadScore";
import { getDealValue, formatINRShort, sumPipelineValue } from "../../utils/crmDealValue";
import CrmLossReasonModal from "./CrmLossReasonModal";
import CrmNextBestAction from "./CrmNextBestAction";
import { buildLossExtra1 } from "../../utils/crmLossReason";
import { logCrmContactActivity } from "../../utils/crmContactActivity";
import { daysInStage, formatDaysInStage } from "../../utils/crmDaysInStage";
import { getLeadAttribution } from "../../utils/crmLeadMeta";

/**
 * Pipeline (Kanban) board for the CRM.
 *
 * Renders non-archived leads grouped into stage columns and lets the user drag
 * a lead between stages. Dropping a lead in a new column updates its `extra`
 * status through the existing /leads/change/:id endpoint (which also handles
 * the convert-to-customer flow on the backend), so no schema changes needed.
 */

const DEFAULT_STAGES = [
    { key: "", label: "Uncontacted", accent: "#6366f1" },
    { key: "Pending", label: "Pending", accent: "#3b82f6" },
    { key: "In Progress", label: "In Progress", accent: "#f59e0b" },
    { key: "Converted", label: "Converted", accent: "#10b981", isClosed: true },
    { key: "Not Interested", label: "Not Interested", accent: "#ef4444", isClosed: true },
];

const UNCONTACTED_ID = "__uncontacted__";

// Droppable ids must be non-empty strings, so the empty "Uncontacted" stage
// gets a sentinel id that we translate back to "" when persisting.
const droppableIdFor = (stageKey) => stageKey || UNCONTACTED_ID;
const statusFromDroppableId = (droppableId) => (droppableId === UNCONTACTED_ID ? "" : droppableId);

const relativeTime = (timestamp) => {
    if (!timestamp) return "";
    const m = moment(timestamp).tz("Asia/Kolkata");
    return m.isValid() ? m.fromNow() : "";
};

const stageKeyOf = (lead, stages) => {
    const extra = String(lead.extra || "").trim();
    return stages.some((s) => s.key === extra) ? extra : "";
};

const CRMPipelineBoard = ({ leads = [], setLeads, url, token, onAfterChange, stages: stagesProp }) => {
    const stages = stagesProp?.length ? stagesProp : DEFAULT_STAGES;
    const [savingId, setSavingId] = useState(null);
    const [lossModal, setLossModal] = useState(null);

    const openLeads = useMemo(
        () => leads.filter((lead) => !(lead.archieve === true || lead.archieve === "true")),
        [leads]
    );

    const totalPipelineValue = useMemo(
        () => sumPipelineValue(openLeads.filter((l) => {
            const s = String(l.extra || "").toLowerCase();
            const closedKeys = stages
                .filter((st) => st.isClosed)
                .map((st) => String(st.key || st.label || "").toLowerCase());
            if (closedKeys.length) {
                return !closedKeys.includes(s);
            }
            return s !== "converted" && s !== "not interested" && s !== "cancelled";
        })),
        [openLeads, stages]
    );

    const columns = useMemo(() => {
        const grouped = {};
        stages.forEach((s) => {
            grouped[s.key] = [];
        });
        leads
            .filter((lead) => !(lead.archieve === true || lead.archieve === "true"))
            .forEach((lead) => {
                grouped[stageKeyOf(lead, stages)].push(lead);
            });
        // Highest score first within each column.
        Object.keys(grouped).forEach((key) => {
            grouped[key].sort((a, b) => getLeadScore(b).score - getLeadScore(a).score);
        });
        return grouped;
    }, [leads, stages]);

    const updateStatus = async (lead, newStatus, lossExtra1 = null) => {
        const previousStatus = lead.extra || "";
        setSavingId(lead.id);

        setLeads((prev) =>
            prev.map((item) => (item.id === lead.id ? { ...item, extra: newStatus } : item))
        );

        try {
            const payload = { extra: newStatus };
            if (lossExtra1) payload.loss_extra1 = lossExtra1;

            const response = await fetch(`${url}/leads/change/${lead.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(data.message || "Failed to move lead");
            }

            if (data.warning) {
                toast.warning(data.warning);
            } else {
                const label = stages.find((s) => s.key === newStatus)?.label || "Uncontacted";
                toast.success(`Moved to ${label}`);
            }
            onAfterChange?.();
        } catch (error) {
            setLeads((prev) =>
                prev.map((item) => (item.id === lead.id ? { ...item, extra: previousStatus } : item))
            );
            toast.error(error.message || "Could not move lead");
        } finally {
            setSavingId(null);
        }
    };

    const requestStatusChange = (lead, newStatus) => {
        if (String(newStatus).toLowerCase() === "not interested") {
            const name = lead.name || lead.business_name || "Lead";
            setLossModal({ lead, newStatus, name });
            return;
        }
        updateStatus(lead, newStatus);
    };

    const onDragEnd = (result) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId) return;

        const lead = leads.find((l) => String(l.id) === String(draggableId));
        if (!lead) return;

        requestStatusChange(lead, statusFromDroppableId(destination.droppableId));
    };

    const openContact = async (e, lead, channel) => {
        e.stopPropagation();
        const type = channel === "phone" ? "call" : channel;
        const result = await logCrmContactActivity(lead.id, type);
        if (result?.blocked) {
            toast.error(result.message || "Contact blocked (DND)");
            return;
        }
        if (channel === "phone" && lead.mobile) {
            window.location.href = `tel:${lead.mobile}`;
        } else if (channel === "whatsapp" && lead.mobile) {
            const num = String(lead.mobile).replace(/\D/g, "");
            window.open(`https://wa.me/${num.startsWith("91") ? num : `91${num}`}`, "_blank");
        } else if (channel === "email" && lead.email) {
            window.location.href = `mailto:${lead.email}`;
        }
    };

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            {totalPipelineValue > 0 && (
                <div className="crm-board__pipeline-total">
                    <span>Open pipeline value</span>
                    <strong>{formatINRShort(totalPipelineValue)}</strong>
                </div>
            )}
            <div className="crm-board">
                {stages.map((stage) => {
                    const stageLeads = columns[stage.key] || [];
                    const stageValue = stageLeads.reduce((sum, l) => sum + getDealValue(l), 0);
                    const stageValueLabel = formatINRShort(stageValue);

                    return (
                        <Droppable droppableId={droppableIdFor(stage.key)} key={droppableIdFor(stage.key)}>
                            {(provided, snapshot) => (
                                <div
                                    className={`crm-board__column ${snapshot.isDraggingOver ? "crm-board__column--over" : ""}`}
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    <div className="crm-board__column-head" style={{ borderTopColor: stage.accent }}>
                                        <div className="crm-board__column-title">
                                            <span className="crm-board__dot" style={{ background: stage.accent }} />
                                            <span>{stage.label}</span>
                                            <span className="crm-board__count">{stageLeads.length}</span>
                                        </div>
                                        {stageValueLabel && (
                                            <span className="crm-board__column-value">{stageValueLabel}</span>
                                        )}
                                    </div>

                                    <div className="crm-board__column-body">
                                        {stageLeads.map((lead, index) => {
                                            const { tier } = getLeadScore(lead);
                                            const tierStyle = TIER_STYLES[tier] || TIER_STYLES.Cold;
                                            const displayName =
                                                lead.name && lead.name !== "" ? lead.name : lead.business_name || "Unnamed lead";
                                            const valueLabel = formatINRShort(getDealValue(lead));
                                            const sourceLabel = lead.source || lead.enquiry_source;
                                            const stageDays = daysInStage(lead);
                                            const stageDaysLabel = formatDaysInStage(stageDays);
                                            const repName = lead.staff_name || lead.assigned_to;
                                            const attr = getLeadAttribution(lead);
                                            const hasReminder = lead.remainder_date && lead.remainder_time;

                                            return (
                                                <Draggable draggableId={String(lead.id)} index={index} key={lead.id}>
                                                    {(dragProvided, dragSnapshot) => (
                                                        <div
                                                            ref={dragProvided.innerRef}
                                                            {...dragProvided.draggableProps}
                                                            {...dragProvided.dragHandleProps}
                                                            className={`crm-board__card ${dragSnapshot.isDragging ? "crm-board__card--dragging" : ""} ${savingId === lead.id ? "crm-board__card--saving" : ""}`}
                                                        >
                                                            <div className="crm-board__card-top">
                                                                <span className="crm-board__name">{displayName}</span>
                                                                <span
                                                                    className={`crm-board__tier border ${tierStyle.className}`}
                                                                    title="Lead score"
                                                                >
                                                                    {tierStyle.label}
                                                                </span>
                                                            </div>

                                                            {(lead.city || sourceLabel) && (
                                                                <p className="crm-board__meta">
                                                                    {sourceLabel ? <span>{sourceLabel}</span> : null}
                                                                    {sourceLabel && lead.city ? " · " : ""}
                                                                    {lead.city || ""}
                                                                </p>
                                                            )}

                                                            {valueLabel && (
                                                                <p className="crm-board__amount">{valueLabel}</p>
                                                            )}

                                                            <div className="crm-board__deal-meta">
                                                                {stageDaysLabel && (
                                                                    <span className="crm-board__days">{stageDaysLabel}</span>
                                                                )}
                                                                {repName && (
                                                                    <span className="crm-board__rep">{repName}</span>
                                                                )}
                                                                {hasReminder && (
                                                                    <span className="crm-board__reminder">
                                                                        Reminder {lead.remainder_date}
                                                                    </span>
                                                                )}
                                                                {attr?.campaign && (
                                                                    <span className="crm-board__utm" title={attr.source}>
                                                                        {attr.campaign}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <CrmNextBestAction lead={lead} compact />

                                                            <div className="crm-board__card-foot">
                                                                <span className="crm-board__time">
                                                                    {relativeTime(lead.createdAt)}
                                                                </span>
                                                                <div className="crm-board__actions">
                                                                    {lead.mobile && (
                                                                        <button
                                                                            type="button"
                                                                            title="Call"
                                                                            onClick={(e) => openContact(e, lead, "phone")}
                                                                        >
                                                                            <i className="fas fa-phone" />
                                                                        </button>
                                                                    )}
                                                                    {lead.mobile && (
                                                                        <button
                                                                            type="button"
                                                                            title="WhatsApp"
                                                                            onClick={(e) => openContact(e, lead, "whatsapp")}
                                                                        >
                                                                            <i className="fab fa-whatsapp" />
                                                                        </button>
                                                                    )}
                                                                    {lead.email && (
                                                                        <button
                                                                            type="button"
                                                                            title="Email"
                                                                            onClick={(e) => openContact(e, lead, "email")}
                                                                        >
                                                                            <i className="fas fa-envelope" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}

                                        {stageLeads.length === 0 && !snapshot.isDraggingOver && (
                                            <div className="crm-board__empty">Drop leads here</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </Droppable>
                    );
                })}
            </div>

            <CrmLossReasonModal
                open={Boolean(lossModal)}
                leadName={lossModal?.name}
                onCancel={() => setLossModal(null)}
                onConfirm={(reasonId, note) => {
                    if (!lossModal) return;
                    updateStatus(lossModal.lead, lossModal.newStatus, buildLossExtra1(reasonId, note));
                    setLossModal(null);
                }}
            />
        </DragDropContext>
    );
};

export default CRMPipelineBoard;
